import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import { analyticsApis } from "@services/apis/Analytics";
import {
  GetUnsweptBalanceData,
  GetUnsweptBalanceModel,
} from "@services/models/Prime/GetUnsweptBalanceModel";
import { ColDef } from "ag-grid-community";
import { useEffect, useMemo, useState } from "react";
import BalanceSum from "../components/BalanceSum";

const PlatformBalances = () => {
  const [merchantLoading, setMerchantLoading] = useState(false);
  const [personalLoading, setPersonalLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [personalUnsweptBalance, setPersonalUnsweptBalance] = useState<
    GetUnsweptBalanceData[]
  >([]);
  const [merchantUnsweptBalance, setMerchantUnsweptBalance] = useState<
    GetUnsweptBalanceData[]
  >([]);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Crypto",
        field: "cryptoSymbol",
        flex: 1,
        minWidth: 120,
        maxWidth: 180,
        cellRenderer: (params: any) => {
          const { data } = params;
          return (
            <div className="flex items-center h-full gap-2">
              <img
                src={data.currencyImg}
                alt={data.cryptoSymbol}
                className="w-[10px] h-[10px]"
              />
              <span className="text-[10px] text-text-base font-medium">
                {data.cryptoSymbol}
              </span>
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Network",
        field: "networkSymbol",
        flex: 1,
        minWidth: 120,
        maxWidth: 180,
        cellRenderer: (params: any) => {
          const { data } = params;
          return (
            <div className="flex items-center h-full gap-2">
              <img
                src={data.networkImg}
                alt={data.networkSymbol}
                className="w-[10px] h-[10px]"
              />
              <span className="text-[10px] text-text-base font-medium">
                {data.networkSymbol}
              </span>
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Merchant Amount",
        field: "merchantUnsweptAmount",
        flex: 1.2,
        minWidth: 140,
        maxWidth: 220,
        valueFormatter: (params) => {
          const value = params.value;
          return typeof value === "number" && !isNaN(value)
            ? value.toFixed(2)
            : "0.00";
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        hide: true,
      },
      {
        headerName: "Personal Amount",
        field: "personalUnsweptAmount",
        flex: 1.2,
        minWidth: 140,
        maxWidth: 220,
        valueFormatter: (params) => {
          const value = params.value;
          return typeof value === "number" && !isNaN(value)
            ? value.toFixed(2)
            : "0.00";
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        hide: true,
      },
      {
        headerName: "Unswept Amount",
        field: "unsweptAmount",
        flex: 1,
        minWidth: 110,
        maxWidth: 170,
        valueFormatter: (params) => {
          const value = params.value;
          return typeof value === "number" && !isNaN(value)
            ? value.toFixed(2)
            : "0.00";
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
    ],
    [],
  );

  const merchantColumnDefs: ColDef[] = useMemo(
    () =>
      columnDefs.map((col) => {
        if (col.field === "unsweptAmount") {
          return { ...col, field: "merchantUnsweptAmount" };
        }
        if (col.field === "merchantUnsweptAmount") {
          return { ...col, hide: false };
        }
        if (col.field === "personalUnsweptAmount") {
          return { ...col, hide: true };
        }
        return col;
      }),
    [columnDefs],
  );

  const personalColumnDefs: ColDef[] = useMemo(
    () =>
      columnDefs.map((col) => {
        if (col.field === "unsweptAmount") {
          return { ...col, field: "personalUnsweptAmount" };
        }
        if (col.field === "personalUnsweptAmount") {
          return { ...col, hide: false };
        }
        if (col.field === "merchantUnsweptAmount") {
          return { ...col, hide: true };
        }
        return col;
      }),
    [columnDefs],
  );

  const getMerchantUnsweptBalance = async () => {
    try {
      setMerchantLoading(true);
      const response: GetUnsweptBalanceModel =
        await analyticsApis.getMerchantUnsweptBalance();
      setMerchantUnsweptBalance(response.data.unsweptBalances);
    } catch (error: any) {
      setError(
        error?.response?.data?.msg || "Failed to fetch merchant balances",
      );
    } finally {
      setMerchantLoading(false);
    }
  };

  const getPersonalUnsweptBalance = async () => {
    try {
      setPersonalLoading(true);
      const response: GetUnsweptBalanceModel =
        await analyticsApis.getPersonalUnsweptBalance();
      setPersonalUnsweptBalance(response.data.unsweptBalances);
    } catch (error: any) {
      setError(
        error?.response?.data?.msg || "Failed to fetch personal balances",
      );
    } finally {
      setPersonalLoading(false);
    }
  };

  useEffect(() => {
    getPersonalUnsweptBalance();
    getMerchantUnsweptBalance();
  }, []);

  return (
    <div className="flex flex-col max-h-[calc(100vh-60px)]">
      <div className="flex flex-col py-4 overflow-auto">
        <BalanceSum />

        <div className="flex flex-row gap-6 items-start flex-wrap mt-6">
          <div className="flex flex-col w-[48%] min-w-[460px] max-w-full">
            <div className="flex flex-col mb-6">
              <div className="flex flex-row justify-between items-center mb-[16px]">
                <span className="text-[12px] font-semibold">
                  Merchant Unswept Balances
                </span>
              </div>

              <div className="h-[400px] overflow-hidden">
                {merchantLoading ? (
                  <AgGridSkeleton
                    columnDefs={merchantColumnDefs}
                    style={{ height: "100%", width: "100%" }}
                  />
                ) : (
                  <AgGridReactTable
                    rowData={merchantUnsweptBalance}
                    columnDefs={merchantColumnDefs}
                    compact={true}
                    error={error}
                    getRowId={(params) =>
                      `merchant-${params.data.cryptoSymbol}-${params.data.networkSymbol}`
                    }
                  />
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col w-[48%] min-w-[460px] max-w-full">
            <div className="flex flex-col mb-6">
              <div className="flex flex-row justify-between items-center mb-[16px]">
                <span className="text-[12px] font-semibold">
                  Personal Unswept Balances
                </span>
              </div>

              <div className="h-[400px] overflow-hidden">
                {personalLoading ? (
                  <AgGridSkeleton
                    columnDefs={personalColumnDefs}
                    style={{ height: "100%", width: "100%" }}
                  />
                ) : (
                  <AgGridReactTable
                    rowData={personalUnsweptBalance}
                    columnDefs={personalColumnDefs}
                    compact={true}
                    error={error}
                    getRowId={(params) =>
                      `personal-${params.data.cryptoSymbol}-${params.data.networkSymbol}`
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformBalances;
